import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAppForm } from "@/components/ui/tanstack-form";
import { useEffect } from "react";
import { z } from "zod";
import { LicenseTypeTemplatesSection } from "./license-type-templates-section";

const LicenseTypeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  requires_property: z.boolean(),
  effective_date_label: z.string().min(1, "Effective Date Label is required"),
  effective_end_date_label: z
    .string()
    .min(1, "Effective End Date Label is required"),
  is_deleted: z.boolean(),
});

export interface LicenseTypeFormData {
  name: string;
  requires_property: boolean;
  effective_date_label: string;
  effective_end_date_label: string;
  is_deleted: boolean;
}

interface LicenseTypeFormProps {
  licenseTypeId: string | null;
  licenseTypeData: LicenseTypeFormData;
  onSubmit: (data: LicenseTypeFormData) => Promise<void>;
  onCancel: () => void;
}

export function LicenseTypeForm({
  licenseTypeId,
  licenseTypeData,
  onSubmit,
  onCancel,
}: LicenseTypeFormProps) {
  const form = useAppForm({
    defaultValues: licenseTypeData,
    validators: {
      onSubmit: LicenseTypeSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  // Reset form when licenseTypeData changes (for add, edit)
  useEffect(() => {
    const resetValues = {
      name: licenseTypeData.name,
      requires_property: licenseTypeData.requires_property,
      effective_date_label: licenseTypeData.effective_date_label,
      effective_end_date_label: licenseTypeData.effective_end_date_label,
      is_deleted: licenseTypeData.is_deleted,
    };

    form.reset(resetValues);
  }, [licenseTypeData, form.reset]);

  const handleSubmit = async () => {
    await form.handleSubmit();
  };

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <form.AppForm>
            <form.Subscribe>
              {() => (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <form.AppField
                      name="name"
                      validators={{
                        onChange: LicenseTypeSchema.shape.name,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="Name">
                          <Input
                            id={field.name}
                            placeholder="Enter license type name"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>

                    <form.AppField name="requires_property">
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Requires Property"
                        >
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={field.name}
                              checked={field.state.value}
                              onCheckedChange={field.handleChange}
                            />
                            <Label htmlFor={field.name}>
                              {field.state.value ? "Yes" : "No"}
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>

                    <form.AppField
                      name="effective_date_label"
                      validators={{
                        onChange: LicenseTypeSchema.shape.effective_date_label,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Effective Date Label"
                        >
                          <Input
                            id={field.name}
                            placeholder="e.g., Start Date, Issue Date"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>

                    <form.AppField
                      name="effective_end_date_label"
                      validators={{
                        onChange:
                          LicenseTypeSchema.shape.effective_end_date_label,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Effective End Date Label"
                        >
                          <Input
                            id={field.name}
                            placeholder="e.g., End Date, Expiry Date"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </div>

                  {/* Active/Delete Section */}
                  <div className="space-y-4">
                    <form.AppField name="is_deleted">
                      {(field) => (
                        <FormFieldWrapper field={field} label="Status">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={field.name}
                              checked={!field.state.value}
                              onCheckedChange={(checked) =>
                                field.handleChange(!checked)
                              }
                            />
                            <Label htmlFor={field.name}>
                              {!field.state.value ? "Active" : "Inactive"}
                            </Label>
                          </div>
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </div>
                </div>
              )}
            </form.Subscribe>
          </form.AppForm>
        </CardContent>
        {/* Document Templates Section - only show when editing existing license type */}
        {licenseTypeId && (
          <div className="pt-6 border-t">
            <LicenseTypeTemplatesSection licenseTypeId={licenseTypeId} />
          </div>
        )}
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                className="disabled:opacity-20 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting
                  ? !licenseTypeId
                    ? "Creating..."
                    : "Updating..."
                  : !licenseTypeId
                    ? "Create License Type"
                    : "Update License Type"}
              </Button>
            )}
          </form.Subscribe>
        </CardFooter>
      </Card>
    </>
  );
}
